import { useTranslate } from "@tolgee/react";
import { cn } from "#/lib/utils";
import {
	TradePopoverSelect,
	type TradePopoverSelectOption,
} from "../trade-popover-select.tsx";
import type { AmountInputMode } from "./hooks/use-order-form-state.ts";
import { sanitizeNumericInput } from "./utils/sanitize-numeric-input.ts";

const inputClass =
	"w-full bg-transparent text-label1-bold text-trade-text outline-none placeholder:text-trade-text-faint sm:text-body2-bold";

const fieldClass =
	"flex items-center gap-1.5 rounded-[8px] border border-trade-border-subtle bg-trade-surface-inset px-2.5 py-1.5 sm:gap-2 sm:rounded-[10px] sm:px-3.5 sm:py-2";

type SizeInputProps = {
	amount: string;
	quoteAmount: string;
	coin: string;
	amountInputMode: AmountInputMode;
	onAmountInputModeChange: (mode: AmountInputMode) => void;
	onAmountChange: (value: string) => void;
	onQuoteAmountChange: (value: string) => void;
	szDecimals: number;
};

export function SizeInput({
	amount,
	quoteAmount,
	coin,
	amountInputMode,
	onAmountInputModeChange,
	onAmountChange,
	onQuoteAmountChange,
	szDecimals,
}: SizeInputProps) {
	const { t } = useTranslate();
	const isUsdc = amountInputMode === "quoteAmount";
	const displayValue = isUsdc ? quoteAmount : amount;
	const maxDecimals = isUsdc ? 2 : szDecimals;
	const unitOptions: Array<TradePopoverSelectOption<AmountInputMode>> = [
		{ value: "quoteAmount", label: "USDC" },
		{ value: "amount", label: coin },
	];

	return (
		<label className={fieldClass}>
			<p className="whitespace-nowrap text-label2 sm:text-body2">
				{t("form.size")}
			</p>
			<input
				type="text"
				inputMode="decimal"
				className={cn(inputClass, "text-right")}
				value={displayValue}
				onChange={(e) => {
					const sanitized = sanitizeNumericInput(e.target.value, maxDecimals);
					if (isUsdc) {
						onQuoteAmountChange(sanitized);
					} else {
						onAmountChange(sanitized);
					}
				}}
				placeholder="0.00"
			/>
			<TradePopoverSelect
				ariaLabel={t("form.size")}
				align="end"
				onValueChange={onAmountInputModeChange}
				options={unitOptions}
				sideOffset={4}
				triggerClassName={cn(
					"w-auto shrink-0 gap-1 border-0 bg-transparent px-0 py-0 text-trade-text shadow-none hover:border-transparent hover:bg-transparent hover:text-trade-text focus-visible:border-transparent focus-visible:ring-0 [&_[data-slot=select-value]]:line-clamp-none [&_[data-slot=select-value]]:whitespace-nowrap [&_[data-slot=select-value]]:text-trade-text [&_[data-slot=popover-trigger-icon]]:size-3.5 [&_[data-slot=popover-trigger-icon]]:text-trade-text-secondary",
					"text-label2-bold sm:text-body2-bold [&_[data-slot=popover-trigger-icon]]:size-3 sm:[&_[data-slot=popover-trigger-icon]]:size-3.5",
				)}
				value={amountInputMode}
			/>
		</label>
	);
}
